/**
 * input.js - Mouse-Tracking, Resize, Mobile Detection, Keyboard Controls
 */
import { settings, IMPULSE_DECAY } from "./settings.js";
import { onWindowResize } from "./scene.js";
import { changeDonutSize, currentMesh } from "./mesh.js";
import { triggerDissolve } from "./dissolve.js";
import {
  morphToNextLetter as particleMorphNext,
  morphToPreviousLetter as particleMorphPrev,
  triggerParticleDissolve,
  getCurrentLetterMesh
} from "./particleMorph.js";
import { adjustWordDisplayCamera } from "./wordDisplay.js";

// Mouse State
let mouseX = 0;
let mouseY = 0;
export let mapX = 0;
export let mapY = 0;

// Rotation Impulse
let lastMouseX = window.innerWidth / 2;
export let rotationImpulse = 0;

// Auto-Play Timer
let autoPlayInterval = null;

/**
 * Prüft ob Mobile Device
 */
export function isMobileDevice() {
  return window.innerWidth <= 480;
}

/**
 * Mouse Move Handler
 */
function onDocumentMouseMove(event) {
  const windowX = window.innerWidth;
  const windowY = window.innerHeight;

  mouseX = event.clientX - windowX;
  mouseY = event.clientY - windowY;

  // Map X: -3 bis 8
  mapX = Math.ceil(((mouseX - (-windowX)) * (8 - (-3))) / (windowX - (-windowX)) + (-3));

  // Map Y: 0 bis 15
  mapY = ((mouseY - (-windowY)) * (15 - 0)) / (windowY - (-windowY)) + 0;
}

/**
 * Rotation Impulse Handler
 */
function onMouseMoveRotation(event) {
  const newX = event.clientX;
  const deltaX = newX - lastMouseX;
  lastMouseX = newX;
  rotationImpulse += deltaX * 0.00009;
}

/**
 * Decay für Rotation Impulse (wird in Animation aufgerufen)
 */
export function decayRotationImpulse() {
  rotationImpulse *= IMPULSE_DECAY;
}

/**
 * Passt Größe für Mobile an
 */
export function adjustForMobile() {
  if (window.innerWidth <= 768) {
    changeDonutSize(0.45);
  }
}

/**
 * Resize Handler mit Mobile-Anpassungen
 */
function handleResize() {
  onWindowResize();
  adjustForMobile();

  // Word-Display Kamera anpassen
  if (settings.wordDisplay) {
    adjustWordDisplayCamera();
  }

  if (isMobileDevice()) {
    settings.mouseIntensity = true;
    settings.verticesIntensity = 15;
  } else {
    settings.verticesIntensity = 0;
  }
}

/**
 * Linksklick Handler - Dissolve auslösen
 */
function onMouseClick(event) {
  // Nur Linksklick (button 0)
  if (event.button !== 0) return;

  // Partikel-Morph Modus: Klick = Dissolve (wenn aktiviert)
  if (settings.particleMorph) {
    if (settings.dissolve) {
      const letterMesh = getCurrentLetterMesh();
      if (letterMesh) {
        triggerDissolve(letterMesh);
      } else {
        // Kein Mesh sichtbar - triggerParticleDissolve für Haar-Explosion
        triggerParticleDissolve();
      }
    }
    return;
  }

  // Dissolve nur wenn aktiviert (Donut-Modus)
  if (settings.dissolve && currentMesh) {
    triggerDissolve(currentMesh);
  }
}

/**
 * Keyboard Handler - Pfeiltasten und Space
 */
function onKeyDown(event) {
  // Nur im Partikel-Morph Modus
  if (!settings.particleMorph) return;

  switch (event.code) {
    case "ArrowRight":
      // Nächster Buchstabe
      particleMorphNext();
      break;

    case "ArrowLeft":
      // Vorheriger Buchstabe
      particleMorphPrev();
      break;

    case "Space":
      // Auto-Play Toggle
      event.preventDefault();  // Verhindert Scrollen
      toggleAutoPlay();
      break;
  }
}

/**
 * Startet/Stoppt Auto-Play
 */
function toggleAutoPlay() {
  settings.particleMorphAutoPlay = !settings.particleMorphAutoPlay;

  if (settings.particleMorphAutoPlay) {
    console.log("Auto-play started");
    // Sofort zum nächsten wechseln
    particleMorphNext();
    // Dann alle X Sekunden
    autoPlayInterval = setInterval(() => {
      particleMorphNext();
    }, settings.particleMorphAutoInterval);
  } else {
    console.log("Auto-play stopped");
    if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
    }
  }
}

/**
 * Startet Auto-Play (von außen aufrufbar)
 */
export function startAutoPlay() {
  if (autoPlayInterval) return; // Bereits aktiv

  settings.particleMorphAutoPlay = true;
  console.log("Auto-play started");
  // Sofort zum nächsten wechseln
  particleMorphNext();
  // Dann alle X Sekunden
  autoPlayInterval = setInterval(() => {
    particleMorphNext();
  }, settings.particleMorphAutoInterval);
}

/**
 * Stoppt Auto-Play (von außen aufrufbar)
 */
export function stopAutoPlay() {
  if (!autoPlayInterval) return; // Bereits gestoppt

  settings.particleMorphAutoPlay = false;
  console.log("Auto-play stopped");
  clearInterval(autoPlayInterval);
  autoPlayInterval = null;
}

/**
 * Initialisiert Auto-Play wenn in Settings aktiviert
 * Muss nach dem Laden der Buchstaben aufgerufen werden
 */
export function initAutoPlay() {
  if (settings.particleMorphAutoPlay && settings.particleMorph) {
    console.log("Auto-play initializing from settings...");
    // Interval starten (ohne sofort zu wechseln, da wir schon beim ersten Buchstaben sind)
    autoPlayInterval = setInterval(() => {
      particleMorphNext();
    }, settings.particleMorphAutoInterval);
  }
}

/**
 * Initialisiert alle Input-Events
 */
export function initInput() {
  document.addEventListener("mousemove", onDocumentMouseMove);
  document.addEventListener("mousemove", onMouseMoveRotation);
  document.addEventListener("click", onMouseClick);
  document.addEventListener("keydown", onKeyDown);
  window.addEventListener("resize", handleResize);

  // Initial Mobile-Anpassung
  adjustForMobile();

  window.addEventListener("DOMContentLoaded", () => {
    setTimeout(adjustForMobile, 100);
  });
}
